import { useMemo, type JSX } from 'react';
import { Navigate, Outlet, useOutletContext } from 'react-router-dom';
import { ErrorState, Skeleton } from '@/components/feedback';
import { DashboardShell } from '@/components/dashboard/DashboardShell';
import { useAuth } from '@/providers/AuthProvider';
import { USER_ROLES, type AppUser } from '@/lib/auth';

const loadingShell = (
  <DashboardShell>
    <section className="grid gap-xl py-12">
      <header className="flex flex-col gap-sm">
        <Skeleton className="h-10 w-64 rounded-xl" aria-label="Loading dashboard" />
        <Skeleton className="h-6 w-96 rounded-xl" />
      </header>
      <div className="grid gap-lg md:grid-cols-2">
        <Skeleton className="h-48 rounded-2xl" />
        <Skeleton className="h-48 rounded-2xl" />
        <Skeleton className="h-48 rounded-2xl md:col-span-2" />
      </div>
    </section>
  </DashboardShell>
);

export function DashboardRouter(): JSX.Element {
  const { status, user, isInitializing } = useAuth();
  const contextValue = useMemo<DashboardOutletContext | null>(
    () => (user ? { currentUser: user } : null),
    [user],
  );

  if (status === 'loading' || isInitializing) {
    return loadingShell;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!SUPPORTED_ROLES.includes(user.role as (typeof SUPPORTED_ROLES)[number])) {
    return (
      <ErrorState
        title="Unsupported role"
        description="This account is assigned to an unsupported role. Contact an administrator for help."
      />
    );
  }

  if (!contextValue) {
    throw new Error('DashboardRouter could not establish a user context.');
  }

  return (
    <DashboardShell>
      <Outlet context={contextValue} />
    </DashboardShell>
  );
}

export const DASHBOARD_ROLES = [USER_ROLES.administrator, USER_ROLES.manager] as const;

const SUPPORTED_ROLES = DASHBOARD_ROLES;

interface DashboardOutletContext {
  currentUser: AppUser;
}

export function useDashboardUser(): AppUser {
  const context = useOutletContext<DashboardOutletContext | undefined>();

  if (!context) {
    throw new Error('useDashboardUser must be used inside a DashboardRouter outlet.');
  }

  return context.currentUser;
}
